export default function Terms() {
  return (
    <div className="px-6 py-20">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-16 px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">Terms & Conditions</h1>
          <p className="mt-4 text-slate-400">Last updated: June 8, 2026</p>
        </div>

        {/* TL;DR Summary */}
        <div className="glass-elevated rounded-3xl p-8 mb-12 border-emerald-400/20">
          <h2 className="text-2xl font-bold text-white mb-4">TL;DR — The Basics</h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Use SwiftDev Toolkit for your iOS development work. Don't reverse engineer it or resell it. We're not liable if your build fails. Premium features require a subscription. Standard App Store terms apply.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              By downloading, installing, or using SwiftDev Toolkit, you agree to these Terms & Conditions. If you don't agree, don't use the app.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. License Grant</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We grant you a non-exclusive, non-transferable, revocable license to use SwiftDev Toolkit for your personal or commercial iOS development work, subject to these terms and the Apple App Store Terms of Service.
            </p>
            <p className="text-slate-300 leading-relaxed">
              You may <strong className="text-white">not</strong>:
            </p>
            <ul className="space-y-2 text-slate-300 mt-4">
              <li>• Reverse engineer, decompile, or disassemble the app</li>
              <li>• Modify, adapt, or create derivative works</li>
              <li>• Resell, redistribute, or sublicense the app</li>
              <li>• Remove or alter any copyright, trademark, or proprietary notices</li>
              <li>• Use the app for illegal or unauthorized purposes</li>
            </ul>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. Premium Features & Subscriptions</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              SwiftDev Toolkit offers optional premium features via in-app purchase subscription:
            </p>
            <ul className="space-y-2 text-slate-300">
              <li>• Subscriptions are managed through the Apple App Store</li>
              <li>• Pricing and billing terms are shown before purchase</li>
              <li>• Subscriptions auto-renew unless canceled 24 hours before renewal</li>
              <li>• Manage or cancel subscriptions in App Store Settings</li>
              <li>• No refunds for partial subscription periods</li>
              <li>• Premium features are subject to change</li>
            </ul>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Disclaimer of Warranties</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              SwiftDev Toolkit is provided <strong className="text-white">"AS IS"</strong> and <strong className="text-white">"AS AVAILABLE"</strong> without warranties of any kind, express or implied, including but not limited to:
            </p>
            <ul className="space-y-2 text-slate-300">
              <li>• Merchantability or fitness for a particular purpose</li>
              <li>• Accuracy, reliability, or completeness of build reports</li>
              <li>• Uninterrupted or error-free operation</li>
              <li>• Compatibility with all Xcode projects or configurations</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              You use the app at your own risk. We recommend testing builds in a safe environment before production deployment.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Limitation of Liability</h2>
            <p className="text-slate-300 leading-relaxed">
              To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising from your use or inability to use SwiftDev Toolkit, even if we've been advised of the possibility of such damages.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. Third-Party Tools</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              SwiftDev Toolkit integrates with third-party tools and services:
            </p>
            <ul className="space-y-2 text-slate-300">
              <li>• <strong className="text-white">Xcode & xcodebuild:</strong> Apple Inc. (required)</li>
              <li>• <strong className="text-white">SwiftLint:</strong> Realm Inc. (MIT License, optional)</li>
              <li>• <strong className="text-white">xcrun simctl:</strong> Apple Inc. (required for simulator management)</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              You are responsible for complying with the terms of these third-party tools. We are not responsible for their availability, functionality, or changes.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">7. Updates & Changes</h2>
            <p className="text-slate-300 leading-relaxed">
              We may update SwiftDev Toolkit from time to time to add features, fix bugs, or improve performance. Updates may change or remove features. We may also modify these Terms & Conditions at any time. Continued use after changes constitutes acceptance.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">8. Termination</h2>
            <p className="text-slate-300 leading-relaxed">
              We reserve the right to terminate or suspend your access to SwiftDev Toolkit at any time, with or without notice, for violation of these terms or any other reason. Upon termination, you must cease all use and delete the app.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Governing Law</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms & Conditions are governed by the laws of the jurisdiction in which the developer is located, without regard to conflict of law principles. Any disputes shall be resolved in the courts of that jurisdiction.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">10. Contact</h2>
            <p className="text-slate-300 leading-relaxed">
              If you have questions about these Terms & Conditions, contact us at{' '}
              <a 
                href="mailto:hello@swiftdevtoolkit.com" 
                className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium hover:underline"
              >
                hello@swiftdevtoolkit.com
              </a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
